import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Activity, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Public-facing legal document page (Terms of Service / Privacy Policy).
 *
 * The kind comes from the route ("terms" | "privacy"). Content is fetched
 * from `/api/public/legal/{kind}` in the user's current i18n language so a
 * Spanish-speaking visitor reads the Spanish text on first paint.
 *
 * Phase L: this is what the Signup checkbox links to and what the re-consent
 * modal re-renders for existing users when the platform admin bumps a doc.
 */
export default function LegalPage({ kind = "terms" }) {
  const { t, i18n } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    const lang = (i18n.language || "en").startsWith("es") ? "es" : "en";
    api
      .get(`/public/legal/${kind}`, { params: { lang } })
      .then((r) => {
        if (aborted) return;
        setDoc(r.data || null);
      })
      .catch((e) => {
        if (aborted) return;
        setError(e?.response?.data?.detail || t("legal.fetchFailed", "Could not load document."));
      })
      .finally(() => !aborted && setLoading(false));
    return () => {
      aborted = true;
    };
  }, [kind, i18n.language, t]);

  const heading =
    kind === "terms"
      ? t("legal.termsTitle", "Terms of Service")
      : t("legal.privacyTitle", "Privacy Policy");

  return (
    <div className="min-h-screen bg-background" data-testid={`legal-page-${kind}`}>
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="legal-home-link">
            <Activity className="h-5 w-5 text-[hsl(var(--accent))]" />
            <span className="font-semibold tracking-tight">Elswitch</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              data-testid="legal-back-login"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t("legal.backToLogin", "Back to sign in")}
            </Link>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="rounded-lg border border-border bg-card p-6 sm:p-8">
          <div className="flex items-baseline justify-between gap-4 flex-wrap mb-2">
            {!doc?.content?.trim().startsWith("# ") && (
              <h1 className="text-2xl font-semibold tracking-tight text-foreground" data-testid="legal-title">
                {heading}
              </h1>
            )}
            {doc?.content?.trim().startsWith("# ") && (
              // The markdown already opens with an H1 — render an invisible
              // anchor for tests so we still have a deterministic title node.
              <span className="sr-only" data-testid="legal-title">{heading}</span>
            )}
            {doc?.version ? (
              <span
                className="text-[11px] text-muted-foreground font-mono"
                data-testid="legal-version"
              >
                v{doc.version}
                {doc.updated_at ? ` · ${new Date(doc.updated_at).toLocaleDateString()}` : ""}
              </span>
            ) : null}
          </div>

          {loading ? (
            <div className="space-y-3 mt-6">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : error ? (
            <p className="text-sm text-[hsl(var(--err))] mt-4" data-testid="legal-error">
              {error}
            </p>
          ) : (
            <article className="prose prose-sm max-w-none mt-2" data-testid="legal-content">
              {renderMarkdown(doc?.content || "")}
            </article>
          )}
        </div>

        <footer className="mt-6 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <Link
              to="/terms"
              className="hover:text-foreground"
              data-testid="legal-footer-terms"
            >
              {t("legal.termsTitle", "Terms of Service")}
            </Link>
            <Link
              to="/privacy"
              className="hover:text-foreground"
              data-testid="legal-footer-privacy"
            >
              {t("legal.privacyTitle", "Privacy Policy")}
            </Link>
          </div>
          <span>© {new Date().getFullYear()} Elswitch</span>
        </footer>
      </main>
    </div>
  );
}
